import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { requestHeader } from "../../../../../others/header";
import axios from "axios";
import { message } from "antd";

// Custom error class for Monster API errors
class MonsterAPIError extends Error {
  constructor(message, code, response) {
    super(message);
    this.name = 'MonsterAPIError';
    this.code = code;
    this.response = response;
  }
}

// Error messages mapping
const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error occurred. Please check your connection.',
  INVALID_RESPONSE: 'Invalid response received from server.',
  PARSE_ERROR: 'Error parsing server response.',
  EMPTY_RESPONSE: 'No results found.',
  RATE_LIMIT: 'Rate limit exceeded. Please try again later.',
  UNAUTHORIZED: 'Unauthorized access. Please check your credentials.',
  SERVER_ERROR: 'Server error occurred. Please try again later.',
  DEFAULT: 'An unexpected error occurred.',
  400: 'Invalid request parameters.',
  401: 'Unauthorized access. Please check your credentials.',
  403: 'Access forbidden. Please check your permissions.',
  404: 'Resource not found.',
  429: 'Too many requests. Please try again later.',
  500: 'Internal server error.',
  503: 'Service temporarily unavailable.',
};

export const useSearchFunction = (apiURL) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const requestCount = useRef(0);
  const lastRequestTime = useRef(0);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleError = useCallback((error) => {
    console.error('Search error:', error);

    let errorMessage = ERROR_MESSAGES.DEFAULT;
    let shouldShowUser = true;
    let errorCode = null;

    if (axios.isCancel(error)) {
      shouldShowUser = false;
    } else if (error instanceof MonsterAPIError) {
      errorMessage = error.message;
      errorCode = error.code;
    } else if (error.response) {
      errorCode = error.response.status;
      errorMessage = error.response.data?.message || 
                    ERROR_MESSAGES[errorCode] ||
                    `Server error: ${errorCode}`;

      if (errorCode === 429) {
        const retryAfter = error.response.headers['retry-after'];
        if (retryAfter) {
          errorMessage += ` Please wait ${retryAfter} seconds.`;
        }
      }
    } else if (error.request) {
      errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
      errorCode = 'NETWORK_ERROR';
    }

    if (shouldShowUser) {
      message.error({
        content: errorMessage,
        key: `search-error-${errorCode}`,
        duration: 5
      });
    }

    return { results: [], total: 0, error: errorMessage };
  }, []);

  const validateAndParseResponse = useCallback((responseData, adapter) => {
    if (!responseData) {
      throw new MonsterAPIError(ERROR_MESSAGES.EMPTY_RESPONSE, 'EMPTY_RESPONSE');
    }

    try {
      let dataToProcess = responseData;
      
      if (responseData.data?.Monster?.Resumes) {
        dataToProcess = responseData.data.Monster.Resumes;
      } else if (responseData.Monster?.Resumes) {
        dataToProcess = responseData.Monster.Resumes;
      } else if (responseData.data?.data) {
        dataToProcess = responseData.data.data;
      } else if (responseData.results) {
        dataToProcess = responseData;
      }


      if (!dataToProcess || (typeof dataToProcess !== 'object' && !Array.isArray(dataToProcess))) {
        throw new MonsterAPIError(ERROR_MESSAGES.INVALID_RESPONSE, 'INVALID_RESPONSE');
      }

      const parsedResults = adapter.parseResults(dataToProcess);


      if (!parsedResults) {
        throw new MonsterAPIError(ERROR_MESSAGES.PARSE_ERROR, 'PARSE_ERROR');
      }

      let normalizedResults;
      if (Array.isArray(parsedResults)) {
        normalizedResults = { 
          results: parsedResults, 
          total: parsedResults.length 
        };
      } else if (parsedResults.results) {
        normalizedResults = parsedResults;
      } else {
        normalizedResults = { 
          results: [parsedResults], 
          total: 1 
        };
      }

      if (!Array.isArray(normalizedResults.results)) {
        normalizedResults.results = [];
        normalizedResults.total = 0;
      }

      normalizedResults.results.forEach((result, index) => {
        if (!result.id) {
          console.warn(`Result at index ${index} missing required 'id' field:`, result);
        }
      });

      return normalizedResults;

    } catch (err) {
      console.error('Error parsing response:', err, responseData);
      throw new MonsterAPIError(
        err.message || ERROR_MESSAGES.PARSE_ERROR,
        'PARSE_ERROR',
        responseData
      );
    }
  }, []);

  const search = useCallback(async (params, adapter) => {
    if (!mounted.current) return null;
    
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    try {
      if (!adapter || typeof adapter.formatParams !== 'function') {
        throw new MonsterAPIError('Invalid adapter configuration', 'INVALID_ADAPTER');
      }

      // Rate limiting check
      const now = Date.now();
      if (now - lastRequestTime.current < 1000) { 
        requestCount.current++;
        if (requestCount.current > 5) { 
          throw new MonsterAPIError(ERROR_MESSAGES.RATE_LIMIT, 'RATE_LIMIT');
        }
      } else {
        requestCount.current = 1;
      }
      lastRequestTime.current = now;

      const formattedParams = adapter.formatParams(params);
  

      const response = await axios.get(
        `${apiURL}?${formattedParams}`,
        {
          ...requestHeader(),
          signal: controller.signal,
          timeout: 30000, 
          validateStatus: status => status < 500, 
        }
      );

      if (!mounted.current) return null;

      if (response.status >= 400) {
        throw new MonsterAPIError(
          ERROR_MESSAGES[response.status] || ERROR_MESSAGES.DEFAULT,
          response.status,
          response
        );
      }

      const results = validateAndParseResponse(response.data, adapter);
      
      setData(results);
      return results;

    } catch (err) {
      if (!mounted.current) return null;
      
      const errorResult = handleError(err);
      setError(errorResult.error);
      return errorResult;

    } finally {
      if (mounted.current) {
        setLoading(false);
      }
      controller.abort();
    }
  }, [apiURL, validateAndParseResponse, handleError]);

  return useMemo(() => ({
    search,
    data,
    loading,
    error,
    clearError: () => setError(null)
  }), [search, data, loading, error]);
};